<template>
  <v-container class="app-container--width-max">
    <div class="white rounded-lg elevation-3 ma-3 pa-3 pa-lg-8">
      <div class="d-flex justify-space-between mt-10">
        <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
          <v-icon color="accent" large>mdi-file-find</v-icon>&nbsp;Fact find
        </h1>
      </div>

      <!-- the form -->
      <ValidationObserver ref="factFindForm" v-slot="{ passes }">
        <form @submit.prevent="passes(onSubmit)">
          <p class="mt-5 font-20 font-weight-bold primary--text">
            Current insurance arrangements
          </p>

          <BaseLabel
            text="Do you currently have an insurance policy in place?"
            class="mt-n2 mb-2"
          />
          <BaseToggleButton
            v-model="form.existingInsurancePolicy"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />

          <!-- existing insurance policy -->
          <template v-if="form.existingInsurancePolicy">
            <BaseLabel
              text="When is your policy due to be renewed?"
              class="mt-10 mb-2"
            />
            <BaseInputDate
              ref="renewalDate"
              v-model="form.renewalDate"
              dataUiid=""
              :rules="{
                required: {
                  fieldLabel: 'Renewal date',
                },
                regex: {
                  pattern: '^\\s*$|^(\\d{2})\\/(\\d{2})\\/\\d{4}$',
                  fieldLabel: 'Renewal date',
                },
                date_valid: {
                  fieldLabel: 'Renewal date',
                },
                date_in_range: {
                  max: 1,
                  units: 'years',
                  fieldLabel: 'Renewal date',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            />
            <BaseLabel
              text="Who are you currently insured with?"
              class="mt-3 mb-2"
            />
            <BaseInput
              v-model="form.insuranceProvider"
              hideDetails="auto"
              :rules="{
                required: {
                  fieldLabel: 'Current insurer',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Current insurer"
            />
            <BaseLabel
              text="What cover do you currently have?"
              class="mt-10 mb-2"
            />
            <BaseRadio
              v-model="form.coverType"
              pslStyle="image"
              dataUiid="CoverType"
              class="radio-cover-image"
              :items="setItemDataUiids('CoverType', COVER_TYPE_ITEMS)"
              :imageStyleBorderRadius="10"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Current cover',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '700px' : '',
              }"
              aria-label="Current cover"
            />

            <!-- cover type buildings & contents -->
            <template v-if="isCoverTypeQuestionBuildings">
              <BaseLabel
                text="Current buildings cover amount?"
                class="mt-10 mb-2"
              />
              <BaseInput
                v-model="form.buildingsAmount"
                hideDetails="auto"
                prefix="£"
                moneyFormat
                :rules="{
                  required: {
                    fieldLabel: 'Buildings amount',
                  },
                  regex: {
                    pattern: MONEY_REGEX,
                    fieldLabel: 'Buildings amount',
                  },
                  max_currency_length: {
                    length: 10,
                    fieldLabel: 'Please enter a valid currency amount',
                    errorMessageTemplate:
                      '{0} field may not be greater than 10 characters.',
                  },
                }"
                :style="{
                  'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
                }"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                aria-label="Current buildings cover amount"
              />
            </template>

            <!-- cover type contents only -->
            <template v-if="isCoverTypeQuestionContents">
              <BaseLabel
                text="Current contents cover amount?"
                class="mt-10 mb-2"
              />
              <BaseInput
                v-model="form.contentsAmount"
                hideDetails="auto"
                prefix="£"
                moneyFormat
                :rules="{
                  required: {
                    fieldLabel: 'Contents amount',
                  },
                  regex: {
                    pattern: MONEY_REGEX,
                    fieldLabel: 'Contents amount',
                  },
                  max_currency_length: {
                    length: 10,
                    fieldLabel: 'Please enter a valid currency amount',
                    errorMessageTemplate:
                      '{0} field may not be greater than 10 characters.',
                  },
                }"
                :style="{
                  'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
                }"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                aria-label="Current contents cover amount"
              />
            </template>

            <BaseLabel text="Payment Frequency" class="mt-10 mb-2" />
            <BaseToggleButton
              v-model="form.currentPaymentFrequency"
              :toggleButtonText="{
                yes: 'Monthly',
                yesDataUiid: '',
                no: 'Annually',
                noDataUiid: '',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :min-width="$vuetify.breakpoint.lgAndUp ? '177' : ''"
            />

            <BaseLabel text="Current premium?" class="mt-10 mb-2" />
            <BaseInput
              v-model="form.currentPremium"
              hideDetails="auto"
              prefix="£"
              moneyFormat
              :rules="{
                required: {
                  fieldLabel: 'Current premium',
                },
                regex: {
                  pattern: MONEY_REGEX,
                  fieldLabel: 'Current premium',
                },
                max_currency_length: {
                  length: 8,
                  fieldLabel: 'Please enter a valid currency amount',
                  errorMessageTemplate:
                    '{0} field may not be greater than 8 characters.',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Current premium"
            />
          </template>

          <p class="mt-10 font-20 font-weight-bold primary--text">
            Demands and needs
          </p>

          <BaseLabel
            text="Is it important that you have cover for your buildings?"
            class="mt-n2 mb-2"
          />
          <BaseToggleButton
            v-model="form.buildingsCover"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />

          <!-- AD buildings -->
          <template v-if="form.buildingsCover">
            <BaseLabel
              text="Is it important that you have accidental damage cover for buildings?"
              class="mt-10 mb-2"
            />
            <BaseToggleButton
              v-model="form.buildingsAccidentalCover"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
            />
          </template>

          <BaseLabel
            text="Is it important that you have cover for your contents?"
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="form.contentsCover"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />

          <!-- AD contents -->
          <template v-if="form.contentsCover">
            <BaseLabel
              text="Is it important that you have accidental damage cover for contents?"
              class="mt-10 mb-2"
            />
            <BaseToggleButton
              v-model="form.contentsAccidentalCover"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
            />
          </template>

          <BaseLabel
            text="Would you like cover for landlords emergencies?"
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="form.landlordCoverEmergencies"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />
          <BaseLabel
            text="Would you like cover for legal expenses?"
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="form.landlordCoverLegalExpenses"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />
          <BaseLabel
            text="Would you like cover for rent protection?"
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="form.landlordCoverRentProtection"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />
          <BaseLabel
            text="Is it important that you are able to choose the level of your policy excess?"
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="form.policyExcess"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />
          <BaseLabel
            text="Would you prefer to pay monthly or annually?"
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="form.newPaymentFrequency"
            :toggleButtonText="{
              yes: 'Monthly',
              yesDataUiid: '',
              no: 'Annually',
              noDataUiid: '',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '177' : ''"
          />

          <p class="mt-10 font-20 font-weight-bold primary--text">
            Income &amp; expenditure (Applicant 1)
          </p>

          <BaseLabel text="Monthly net income" class="mt-n2 mb-2" />
          <BaseInput
            v-model="form.landlordApplicant1MonthlyNetIncome"
            hideDetails="auto"
            prefix="£"
            moneyFormat
            :rules="{
              required: {
                fieldLabel: 'Monthly net income',
              },
              regex: {
                pattern: MONEY_REGEX,
                customMessage: 'Please enter a valid currency amount.',
              },
              max_currency_length: {
                length: 8,
                fieldLabel: 'Monthly net income',
                errorMessageTemplate:
                  '{0} field may not be greater than 8 characters.',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Monthly net income"
          />
          <BaseLabel text="Mortgage &amp; home" class="mt-10 mb-2" />
          <BaseInput
            v-model="form.landlordApplicant1MortgageAndHome"
            hideDetails="auto"
            prefix="£"
            moneyFormat
            :rules="{
              required: {
                fieldLabel: 'Mortgage & home',
              },
              regex: {
                pattern: MONEY_REGEX,
                customMessage: 'Please enter a valid currency amount.',
              },
              max_currency_length: {
                length: 8,
                fieldLabel: 'Mortgage & home',
                errorMessageTemplate:
                  '{0} field may not be greater than 8 characters.',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Mortgage and home"
          />
          <BaseLabel text="Lifestyle" class="mt-10 mb-2" />
          <BaseInput
            v-model="form.landlordApplicant1Lifestyle"
            hideDetails="auto"
            prefix="£"
            moneyFormat
            :rules="{
              required: {
                fieldLabel: 'Lifestyle',
              },
              regex: {
                pattern: MONEY_REGEX,
                customMessage: 'Please enter a valid currency amount.',
              },
              max_currency_length: {
                length: 8,
                fieldLabel: 'Lifestyle',
                errorMessageTemplate:
                  '{0} field may not be greater than 8 characters.',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Lifestyle"
          />
          <BaseLabel text="Travel" class="mt-10 mb-2" />
          <BaseInput
            v-model="form.landlordApplicant1Travel"
            hideDetails="auto"
            prefix="£"
            moneyFormat
            :rules="{
              required: {
                fieldLabel: 'Travel',
              },
              regex: {
                pattern: MONEY_REGEX,
                customMessage: 'Please enter a valid currency amount.',
              },
              max_currency_length: {
                length: 8,
                fieldLabel: 'Travel',
                errorMessageTemplate:
                  '{0} field may not be greater than 8 characters.',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Travel"
          />
          <BaseLabel text="Borrowing &amp; debts" class="mt-10 mb-2" />
          <BaseInput
            v-model="form.landlordApplicant1BorrowingAndDebts"
            hideDetails="auto"
            prefix="£"
            moneyFormat
            :rules="{
              required: {
                fieldLabel: 'Borrowing & debts',
              },
              regex: {
                pattern: MONEY_REGEX,
                customMessage: 'Please enter a valid currency amount.',
              },
              max_currency_length: {
                length: 8,
                fieldLabel: 'Borrowing & debts',
                errorMessageTemplate:
                  '{0} field may not be greater than 8 characters.',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Borrowing and debts"
          />
          <BaseLabel text="Day to day expenses" class="mt-10 mb-2" />
          <BaseInput
            v-model="form.landlordApplicant1Expenses"
            hideDetails="auto"
            prefix="£"
            moneyFormat
            :rules="{
              required: {
                fieldLabel: 'Day to day expenses',
              },
              regex: {
                pattern: MONEY_REGEX,
                customMessage: 'Please enter a valid currency amount.',
              },
              max_currency_length: {
                length: 8,
                fieldLabel: 'Day to day expenses',
                errorMessageTemplate:
                  '{0} field may not be greater than 8 characters.',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Day to day expenses"
          />
          <BaseLabel text="Total monthly expenditure" class="mt-10 mb-2" />
          <BaseInput
            class="input-disabled"
            hideDetails="auto"
            prefix="£"
            hideValidationIcon
            :value="getTotalMonthlyExpenditure(applicant1) | formatDecimal"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :rules="{
              max_currency_length: {
                length: 8,
                fieldLabel: 'Total monthly expenditure',
                errorMessageTemplate:
                  '{0} field may not be greater than 8 characters.',
              },
            }"
            aria-label="Total monthly expenditure"
          />
          <BaseLabel text="Spendable income" class="mt-10 mb-2" />
          <BaseInput
            class="input-disabled"
            hideDetails="auto"
            prefix="£"
            hideValidationIcon
            :value="getTotalSpendableIncome(applicant1) | formatDecimal"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :rules="{
              max_currency_length: {
                length: 8,
                fieldLabel: 'Spendable income',
                errorMessageTemplate:
                  '{0} field may not be greater than 8 characters.',
              },
            }"
            aria-label="Spendable income"
          />
          <BaseToggle
            v-model="secondApplicantIncluded.Value"
            label="Add second applicant?"
            class="mt-10"
            @click="resetSecondApplicantIncluded"
          />

          <!-- start second applicant form -->
          <template v-if="secondApplicantIncluded.Value">
            <p class="mt-8 font-20 font-weight-bold primary--text">
              Income &amp; expenditure (Applicant 2)
            </p>

            <BaseLabel text="Monthly net income" class="mt-n2 mb-2" />
            <BaseInput
              v-model="form.landlordApplicant2MonthlyNetIncome"
              hideDetails="auto"
              prefix="£"
              moneyFormat
              :rules="{
                required: {
                  fieldLabel: 'Monthly net income',
                },
                regex: {
                  pattern: MONEY_REGEX,
                  customMessage: 'Please enter a valid currency amount.',
                },
                max_currency_length: {
                  length: 8,
                  fieldLabel: 'Monthly net income',
                  errorMessageTemplate:
                    '{0} field may not be greater than 8 characters.',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Monthly net income"
            />
            <BaseLabel text="Mortgage &amp; home" class="mt-10 mb-2" />
            <BaseInput
              v-model="form.landlordApplicant2MortgageAndHome"
              hideDetails="auto"
              prefix="£"
              moneyFormat
              :rules="{
                required: {
                  fieldLabel: 'Mortgage & home',
                },
                regex: {
                  pattern: MONEY_REGEX,
                  customMessage: 'Please enter a valid currency amount.',
                },
                max_currency_length: {
                  length: 8,
                  fieldLabel: 'Mortgage & home',
                  errorMessageTemplate:
                    '{0} field may not be greater than 8 characters.',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Mortgage and home"
            />
            <BaseLabel text="Lifestyle" class="mt-10 mb-2" />
            <BaseInput
              v-model="form.landlordApplicant2Lifestyle"
              hideDetails="auto"
              prefix="£"
              moneyFormat
              :rules="{
                required: {
                  fieldLabel: 'Lifestyle',
                },
                regex: {
                  pattern: MONEY_REGEX,
                  customMessage: 'Please enter a valid currency amount.',
                },
                max_currency_length: {
                  length: 8,
                  fieldLabel: 'Lifestyle',
                  errorMessageTemplate:
                    '{0} field may not be greater than 8 characters.',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Lifestyle"
            />
            <BaseLabel text="Travel" class="mt-10 mb-2" />
            <BaseInput
              v-model="form.landlordApplicant2Travel"
              hideDetails="auto"
              prefix="£"
              moneyFormat
              :rules="{
                required: {
                  fieldLabel: 'Travel',
                },
                regex: {
                  pattern: MONEY_REGEX,
                  customMessage: 'Please enter a valid currency amount.',
                },
                max_currency_length: {
                  length: 8,
                  fieldLabel: 'Travel',
                  errorMessageTemplate:
                    '{0} field may not be greater than 8 characters.',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Travel"
            />
            <BaseLabel text="Borrowing &amp; debts" class="mt-10 mb-2" />
            <BaseInput
              v-model="form.landlordApplicant2BorrowingAndDebts"
              hideDetails="auto"
              prefix="£"
              moneyFormat
              :rules="{
                required: {
                  fieldLabel: 'Borrowing & debts',
                },
                regex: {
                  pattern: MONEY_REGEX,
                  customMessage: 'Please enter a valid currency amount.',
                },
                max_currency_length: {
                  length: 8,
                  fieldLabel: 'Borrowing & debts',
                  errorMessageTemplate:
                    '{0} field may not be greater than 8 characters.',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Borrowing and debts"
            />
            <BaseLabel text="Day to day expenses" class="mt-10 mb-2" />
            <BaseInput
              v-model="form.landlordApplicant2Expenses"
              hideDetails="auto"
              prefix="£"
              moneyFormat
              :rules="{
                required: {
                  fieldLabel: 'Day to day expenses',
                },
                regex: {
                  pattern: MONEY_REGEX,
                  customMessage: 'Please enter a valid currency amount.',
                },
                max_currency_length: {
                  length: 8,
                  fieldLabel: 'Day to day expenses',
                  errorMessageTemplate:
                    '{0} field may not be greater than 8 characters.',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Day to day expenses"
            />
            <BaseLabel text="Total monthly expenditure" class="mt-10 mb-2" />
            <BaseInput
              class="input-disabled"
              hideDetails="auto"
              prefix="£"
              hideValidationIcon
              :value="getTotalMonthlyExpenditure(applicant2) | formatDecimal"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :rules="{
                max_currency_length: {
                  length: 8,
                  fieldLabel: 'Total monthly expenditure',
                  errorMessageTemplate:
                    '{0} field may not be greater than 8 characters.',
                },
              }"
              aria-label="Total monthly expenditure"
            />
            <BaseLabel text="Spendable income" class="mt-10 mb-2" />
            <BaseInput
              class="input-disabled mb-10"
              hideDetails="auto"
              prefix="£"
              hideValidationIcon
              :value="getTotalSpendableIncome(applicant2) | formatDecimal"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :rules="{
                max_currency_length: {
                  length: 8,
                  fieldLabel: 'Spendable income',
                  errorMessageTemplate:
                    '{0} field may not be greater than 8 characters.',
                },
              }"
              aria-label="Spendable income"
            />
          </template>
          <!-- end second applicant form -->

          <p class="mt-8 font-20 font-weight-bold primary--text">
            Specific needs
          </p>

          <BaseLabel
            text="For the purposes of the quote, are there any specific insurance requirements you have?"
            class="mt-n2 mb-2"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '530px' : '100%',
            }"
          />
          <BaseTextarea
            v-model="form.specificNeeds"
            hideDetails="auto"
            hideValidationIcon
            :rules="{
              max: {
                length: 500,
                fieldLabel: 'Specific needs',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '530px' : '100%',
            }"
            aria-label="Specific needs"
          />

          <BaseButton
            depressed
            rounded
            dense
            class="font-weight-bold font-20 pa-7 px-13 mt-10 mb-3"
            type="submit"
            :color="'psorange'"
            :block="$vuetify.breakpoint.lgAndUp ? false : true"
            :text="'UPDATE FACT FIND'"
          />
        </form>
      </ValidationObserver>
    </div>
  </v-container>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseRadio from "@/components/BaseComponents/BaseRadio";
import BaseTextarea from "@/components/BaseComponents/BaseTextarea";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import BaseToggle from "@/components/BaseComponents/BaseToggle";
import { mapState, mapActions, mapGetters } from "vuex";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { LL_FACT_FIND_FORM } from "@/constants/factfind.constants";
import { populateFactFindFromApi } from "@/common/factfind";
import {
  setItemDataUiids,
  COVER_TYPE_ITEMS,
} from "@/constants/component.constants.js";
import { ROUTE_NAME } from "@/constants/route.constants";
import { tryGoToNextStep, convertToBool, roundFloat } from "@/common/utils";
import { CURRENCY_REGEX } from "@/constants/regex-patterns.constants";

export default {
  name: "FactFindPage",
  components: {
    ValidationObserver,
    BaseLabel,
    BaseInputDate,
    BaseButton,
    BaseInput,
    BaseRadio,
    BaseTextarea,
    BaseToggleButton,
    BaseToggle,
  },
  data() {
    return {
      setItemDataUiids,
      COVER_TYPE_ITEMS,
      MONEY_REGEX: CURRENCY_REGEX,
      form: LL_FACT_FIND_FORM,
      applicant1: "landlordApplicant1",
      applicant2: "landlordApplicant2",
      secondApplicantIncluded: null,
      currentFields: [
        "buildingsAmount",
        "contentsAmount",
        "currentPremium",
        "landlordApplicant1MonthlyNetIncome",
        "landlordApplicant1MortgageAndHome",
        "landlordApplicant1Lifestyle",
        "landlordApplicant1Travel",
        "landlordApplicant1BorrowingAndDebts",
        "landlordApplicant1Expenses",
        "landlordApplicant2MonthlyNetIncome",
        "landlordApplicant2MortgageAndHome",
        "landlordApplicant2Lifestyle",
        "landlordApplicant2Travel",
        "landlordApplicant2BorrowingAndDebts",
        "landlordApplicant2Expenses",
      ],
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, [
      "quoteResponse",
      "restoringLastStep",
      "questionMap",
    ]),
    ...mapGetters(QUESTIONS_MODULE, ["allSections"]),
    isCoverTypeQuestionBuildings() {
      return (
        this.form.coverType === "BuildingsAndContents" ||
        this.form.coverType === "BuildingsOnly"
      );
    },
    isCoverTypeQuestionContents() {
      return (
        this.form.coverType === "BuildingsAndContents" ||
        this.form.coverType === "ContentsOnly"
      );
    },
  },
  watch: {
    "form.existingInsurancePolicy"(val) {
      if (!val) {
        this.form.renewalDate = "";
        this.form.insuranceProvider = "";
        this.form.coverType = "";
        this.$refs.renewalDate.resetFieldAll();
      }
    },
    "form.buildingsCover"(val) {
      if (!val) {
        this.form.buildingsAccidentalCover = false;
      }
    },
    "form.contentsCover"(val) {
      if (!val) {
        this.form.contentsAccidentalCover = false;
      }
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, ["submitFactFind"]),
    getTotalMonthlyExpenditure(key) {
      const total =
        this.removeCharacters(this.form[`${key}MortgageAndHome`]) +
        this.removeCharacters(this.form[`${key}Lifestyle`]) +
        this.removeCharacters(this.form[`${key}Travel`]) +
        this.removeCharacters(this.form[`${key}BorrowingAndDebts`]) +
        this.removeCharacters(this.form[`${key}Expenses`]);
      return String(roundFloat(total));
    },
    getTotalSpendableIncome(key) {
      const numberValue = roundFloat(
        Number(this.removeCharacters(this.form[`${key}MonthlyNetIncome`])) -
          Number(this.getTotalMonthlyExpenditure(key))
      );
      return String(numberValue);
    },
    removeCharacters(value) {
      const removeCharacters = value.replaceAll(/[,]/g, "");
      const stringToNum = Number(removeCharacters);
      return value.length ? stringToNum : 0;
    },
    resetSecondApplicantIncluded() {
      Object.keys(this.form)
        .filter((k) => k.includes(this.applicant2))
        .forEach((k) => (this.form[k] = ""));
    },
    getSecondApplicantIncomeExpenditureTotals() {
      if (!this.secondApplicantIncluded.Value) return {};

      return {
        landlordApplicant2TotalMonthlyExpenditure:
          this.getTotalMonthlyExpenditure(this.applicant2),
        landlordApplicant2TotalSpendableIncome: this.getTotalSpendableIncome(
          this.applicant2
        ),
      };
    },
    onSubmit() {
      for (const field of this.currentFields) {
        if (this.form[field]) {
          this.form[field] = this.form[field].replace(/,/g, "");
        }
      }
      this.submitFactFind({
        ...this.form,
        landlordApplicant1TotalMonthlyExpenditure:
          this.getTotalMonthlyExpenditure(this.applicant1),
        landlordApplicant1TotalSpendableIncome: this.getTotalSpendableIncome(
          this.applicant1
        ),
        ...this.getSecondApplicantIncomeExpenditureTotals(),
      });

      this.$router.push({ name: ROUTE_NAME.LANDLORDS.B2B.COVER_TYPE });
    },
  },
  created() {
    if (this.allSections.length) {
      this.secondApplicantIncluded = this.questionMap.SecondApplicantIncluded;

      // when retrieving a quote convert "Boolean Strings" to Booleans
      this.secondApplicantIncluded.Value = convertToBool(
        this.secondApplicantIncluded.Value
      );
    }
  },
  mounted() {
    if (this.quoteResponse?.Answers)
      populateFactFindFromApi(this.quoteResponse.Answers, this.form);

    if (!this.restoringLastStep) {
      return;
    }

    setTimeout(async () => {
      tryGoToNextStep(
        this.$refs.factFindForm,
        async (form) => await form.validate(),
        ROUTE_NAME.LANDLORDS.B2B.COVER_TYPE
      );
    }, 0);
  },
};
</script>

<style lang="scss" scoped>
.input-disabled :deep(.v-input__slot),
.input-disabled :deep(.v-input__slot *) {
  cursor: not-allowed !important;
}
</style>
